import * as React from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { joinWaitlist } from '@/services/api/waitlistApi';
import { Loader2, CheckCircle2 } from 'lucide-react';

/**
 * Embedded in the waitlist blog post. The `code` field is optional and visible —
 * anyone can type one in, and a partner/cohort link (e.g.
 * raisy.team/blog/waitlist?code=ACCELERATOR26) pre-fills it automatically.
 */
export const WaitlistForm = () => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') || '';

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState(codeFromUrl);
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = React.useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    setStatus('submitting');
    setErrorMessage('');
    try {
      const res = await joinWaitlist(name.trim(), email.trim(), code.trim());
      if (res.status) {
        setStatus('done');
      } else {
        setStatus('error');
        setErrorMessage(res.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div className="not-prose flex items-start gap-3 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 px-6 py-5 text-cyan-700 dark:text-cyan-300">
        <CheckCircle2 className="h-5 w-5 shrink-0" />
        <div className="text-sm">
          <p className="font-semibold">You have successfully enrolled.</p>
          <p className="mt-1">
            You won&apos;t get an email right now — we&apos;ll come back to you later when your spot is ready.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="not-prose rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-6"
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Button
          type="submit"
          disabled={status === 'submitting'}
          className="bg-cyan-600 hover:bg-cyan-500 text-white shrink-0"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Joining
            </>
          ) : (
            'Join the waitlist'
          )}
        </Button>
      </div>
      <Input
        type="text"
        placeholder="Invite or partner code (optional)"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className="mt-3"
      />
      {status === 'error' && (
        <p className="mt-3 text-sm text-red-500 dark:text-red-400">{errorMessage}</p>
      )}
    </form>
  );
};
